/** @jsxImportSource @emotion/react */

import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CSSObject } from '@emotion/react';
import Clickable from './clickable';
import useTheme from '../hooks/useTheme';
import RemoteImage from './RemoteImage';
import LocalImage from './localImage';
import environment from '../utils/environment';
import useLanguage from '../hooks/useLanguage';
import appStoreLinks from '../constants/appStoreLinks';
import localStorage from '../utils/localStorage';
import { AppConfigContext } from '../contexts/appConfigContext';
import { AppContext } from '../contexts/appContext';
import useSnowplowTracker from '../hooks/useSnowplowTracker';
import device from '../utils/device';
import { logInfo } from '../utils/remoteLogger';

type BannerContentProps = {
  color: { [key: string]: string };
  language: { get: (key: string) => string };
  closeBanner: () => void;
  redirectToApp: () => void;
};

const BUTTON_STYLE: CSSObject = {
  width: 100,
  height: 38,
  padding: '0 7px',
  marginLeft: 'auto',
  fontSize: 'smaller',
};

const BannerContent: React.FC<BannerContentProps> = ({
  color,
  language,
  closeBanner,
  redirectToApp,
}) => (
  <div
    css={{
      position: 'sticky',
      top: 0,
      backgroundColor: color.LOCAL_WHITE,
      zIndex: 11,
      padding: '10px',
      borderBottom: `2px solid ${color.DIVIDER_LINE}`,
    }}
  >
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        transform: 'translateY(4%)',
      }}
    >
      <Clickable dim renderAs='div' onClick={closeBanner}>
        <LocalImage src='ICON32_CROSS' tint='TEXT' css={{ height: 24 }} />
      </Clickable>

      <LocalImage
        src='REBRAND_APP_ICON'
        alt=''
        css={{ height: 60, width: 60, margin: '0 10px' }}
      />
      <div css={{ fontSize: 'smaller' }}>
        <div css={{ fontWeight: 700 }}>
          {language.get('smart_banner_title')}
        </div>
        <div>{language.get('smart_banner_sub_title')}</div>
        <RemoteImage
          url='https://appresources.kry.se/images/stars.png'
          css={{ height: '14px' }}
        />
      </div>
      <Clickable styleAs='button' onClick={redirectToApp} css={BUTTON_STYLE}>
        {language.get('smart_banner_button')}
      </Clickable>
    </div>
  </div>
);

const SmartBanner = () => {
  // Wait for 24 hours before displaying the banner again if the user closes it
  const waitTimeInMilliseconds = 60 * 60 * 24 * 1000;

  const { color } = useTheme();
  const { appConfig } = useContext(AppConfigContext);
  const { userLoggedIn } = useContext(AppContext);
  const intervalRef = useRef<NodeJS.Timeout | null>();

  const snowplowTracker = useSnowplowTracker();

  const language = useLanguage();

  type AppStoreLinkKeys = keyof typeof appStoreLinks;

  const getInitialIsClosed = () => {
    const storedTime = Number(localStorage.get('isClosedTime'));
    if (!storedTime || Number.isNaN(storedTime)) return false;
    // If 24h have passed since closing, show the banner again
    return Date.now() < storedTime + waitTimeInMilliseconds;
  };

  const [isClosed, setIsClosed] = useState(getInitialIsClosed);

  const getLinkToRedirectToApp = (
    linkType: 'UNIVERSAL_LINK' | 'ADJUST_DEEP_LINK'
  ) => {
    switch (environment.COUNTRY) {
      case 'NO':
        return appStoreLinks[
          `NO_KRY_${linkType}_TO_OPEN_APP` as AppStoreLinkKeys
        ];
      // FR runs on the Livi app, so the deep link is the Livi one even though
      // the universal link is on the rebranded kry.fr host.
      case 'FR':
        return linkType === 'UNIVERSAL_LINK'
          ? appStoreLinks.FR_KRY_UNIVERSAL_LINK_TO_OPEN_APP
          : appStoreLinks.LIVI_ADJUST_DEEP_LINK_TO_OPEN_APP;
      // GB has no consumer web app, and every GB partner sets
      // smartBanner: false, so the banner never renders there and there is no
      // link to open.
      case 'GB':
        return undefined;
      default:
        return appStoreLinks[`KRY_${linkType}_TO_OPEN_APP` as AppStoreLinkKeys];
    }
  };

  const getDeeplinkToRedirectToApp = () =>
    getLinkToRedirectToApp('ADJUST_DEEP_LINK');
  const getUniversalLinkToRedirectToApp = () =>
    getLinkToRedirectToApp('UNIVERSAL_LINK');

  // Use adjust link after user is logged in and adjust is enabled other wise use universal link
  const redirectToInstalledAppOrAppStore = () => {
    const link =
      userLoggedIn && appConfig.adjustState === 'enabled'
        ? getDeeplinkToRedirectToApp()
        : getUniversalLinkToRedirectToApp();

    if (!link) return;

    window.location.href = link;
  };

  const closeBanner = () => {
    setIsClosed(true);
    localStorage.set('isClosedTime', new Date().getTime());
    // Added logInfo to track the banner close event on non-logged in users
    // since tracking is not allowed for non-logged screen
    if (!userLoggedIn) {
      logInfo('SmartBanner', 'Banner closed');
    }

    snowplowTracker.trackEvent(snowplowTracker.event.BANNER_CLOSE);
  };
  const redirectToApp = () => {
    redirectToInstalledAppOrAppStore();
    setIsClosed(true);
    if (!userLoggedIn) {
      logInfo('SmartBanner', 'Redirect to app store');
    }
    snowplowTracker.trackEvent(snowplowTracker.event.BANNER_REDIRECT);
  };
  const resetBannerIfTimeExpired = useCallback(() => {
    const storedTime = Number(localStorage.get('isClosedTime')) || 0;
    if (Date.now() >= storedTime + waitTimeInMilliseconds) {
      localStorage.remove('isClosedTime');
      setIsClosed(false);
    }
  }, [waitTimeInMilliseconds]);

  useEffect(() => {
    // trigger when the component is mounted
    resetBannerIfTimeExpired();

    if (!isClosed && !localStorage.get('isClosedTime')) {
      snowplowTracker.trackEvent(snowplowTracker.event.BANNER_DISPLAYED);
    }

    // Check every 24 hours if the banner should be displayed, and also trigger check when the window regains focus for accuracy
    intervalRef.current = setInterval(
      resetBannerIfTimeExpired,
      waitTimeInMilliseconds
    );
    window.addEventListener('focus', resetBannerIfTimeExpired);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('focus', resetBannerIfTimeExpired);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = null;
    };
  }, [
    resetBannerIfTimeExpired,
    isClosed,
    snowplowTracker,
    waitTimeInMilliseconds,
  ]);

  const shouldShowBanner = !isClosed && device.IS_MOBILE;

  return (
    <Fragment>
      {shouldShowBanner && (
        <BannerContent
          color={color}
          language={language}
          closeBanner={closeBanner}
          redirectToApp={redirectToApp}
        />
      )}
    </Fragment>
  );
};

export default SmartBanner;
